import { useCallback, useMemo, useState } from 'react';

import { useNotifications } from '@decisiv/ui-components';

import {
  ACCOUNT_COLUMN,
  DISCOVERABILITY_KEYS,
  TIER_SUFFIX,
  buildColumns,
} from '../components/AccessPermissions/columns';
import {
  ALL_ACCOUNTS_ROW,
  ALL_TEAMS_ROW,
  LOCKS,
  MESSAGES,
  NOTICES,
} from '../components/AccessPermissions/copy';
import { toAccessErrors } from '../components/AccessPermissions/serverErrors';
import saveAccessPermissions from '../services/accessPermissionsService';

const NOTIFICATION_DURATION = 8000;

const ALL_MODE = 'all';
const CUSTOM_MODE = 'custom';

export const MODES = { all: ALL_MODE, custom: CUSTOM_MODE };

const emptyEntry = () => ({
  roleIds: [],
  visibleAsExternalRecipient: true,
  visibleAsInternalRecipient: true,
});

// Order-insensitive, since ticking A then B must equal ticking B then A.
//
// `only` narrows it to the ids the table can draw: two accounts differing solely
// in a role with no checkbox agree as far as this screen is concerned, and
// comparing the invisible ones would refuse "Apply to All" over a difference the
// admin cannot see, let alone resolve.
const entryKey = (entry, only) => {
  const roleIds = only
    ? entry.roleIds.filter((roleId) => only.includes(roleId))
    : entry.roleIds;

  return [
    [...roleIds].sort().join('|'),
    entry.visibleAsExternalRecipient,
    entry.visibleAsInternalRecipient,
  ].join('#');
};

const sameEntry = (a, b, only) => entryKey(a, only) === entryKey(b, only);

/**
 * The one entry every account shares, or null when they disagree. Carries only
 * the ids the table can draw: the shared row stands for every account at once,
 * so letting the first account's private role ride along would grant it to all
 * of them on the next save.
 */
const agreementOf = (entries, only) => {
  if (entries.length === 0) {
    return null;
  }

  const [first] = entries;

  if (!entries.every((entry) => sameEntry(entry, first, only))) {
    return null;
  }

  return {
    ...first,
    roleIds: only
      ? first.roleIds.filter((roleId) => only.includes(roleId))
      : [...first.roleIds],
  };
};

const toDraft = (access) => {
  const teams = access?.teams || [];
  const accounts = teams.flatMap((team) =>
    team.accounts.map((account) => ({
      ...account,
      teamId: team.id,
      teamName: team.name,
    })),
  );

  const byAccount = accounts.reduce(
    (draft, account) => ({
      ...draft,
      [account.accountId]: {
        roleIds: [...(account.roleIds || [])],
        visibleAsExternalRecipient: Boolean(
          account.discoverability?.visibleAsExternalRecipient,
        ),
        visibleAsInternalRecipient: Boolean(
          account.discoverability?.visibleAsInternalRecipient,
        ),
      },
    }),
    {},
  );

  return { teams, accounts, byAccount };
};

/**
 * The Access & Permissions tab's state (PD-4070).
 *
 * Every rule lives here and leaves as data: each cell arrives already carrying
 * `disabled` and `lockReason`, so the components below are dumb and the guards
 * can be tested without rendering a table. The three-state shape mirrors
 * useEditUserForm.
 *
 * A per-account entry is a flat set of role ids rather than the wizard's
 * {permissionIds, managementRoleId} pair, because the server speaks role ids: it
 * makes the payload the identity, "at least one permission" a length check, and
 * a role with no column survive a save instead of being silently revoked.
 *
 * That last guarantee is why `known` is threaded through this file — it holds in
 * "Apply to All" too, where each account's column-less ids ride across the
 * shared row untouched.
 */
function useAccessPermissionsForm(user, { canEdit = false } = {}) {
  const access = user?.access;

  const initial = useMemo(() => toDraft(access), [access]);

  const [baseline, setBaseline] = useState(initial);
  const [byAccount, setByAccount] = useState(initial.byAccount);
  const [errors, setErrors] = useState({ base: [], byAccount: {} });
  const [isSaving, setIsSaving] = useState(false);
  const [notice, setNotice] = useState(null);

  const visibleAccounts = useMemo(
    () => baseline.accounts.filter((account) => !account.revoked),
    [baseline.accounts],
  );

  const permissionOptions = useMemo(() => access?.permissionOptions || [], [
    access,
  ]);

  /** Every role id the table can draw, so unknown ones can be left alone. */
  const known = useMemo(() => knownRoleIds(permissionOptions), [
    permissionOptions,
  ]);

  const initialShared = useMemo(
    () =>
      agreementOf(
        visibleAccounts.map((account) => baseline.byAccount[account.accountId]),
        known,
      ) || emptyEntry(),
    [baseline.byAccount, known, visibleAccounts],
  );

  const [shared, setShared] = useState(initialShared);
  const [sharedTouched, setSharedTouched] = useState(false);

  // Opens on "Apply to All" only when the record actually agrees. Opening there
  // on accounts that differ would be a lie, and the single row would have to
  // invent a value to show.
  const [mode, setMode] = useState(() =>
    agreementOf(
      visibleAccounts.map((account) => baseline.byAccount[account.accountId]),
      known,
    )
      ? ALL_MODE
      : CUSTOM_MODE,
  );

  // One account has nothing to apply "to all" of. Derived rather than stored, so
  // revoking an account needs no state write to take the radios away.
  const showScopeChoice = visibleAccounts.length > 1;
  const effectiveMode = showScopeChoice ? mode : CUSTOM_MODE;

  const columns = useMemo(() => buildColumns(permissionOptions), [
    permissionOptions,
  ]);

  const isSelf = Boolean(access?.isSelf);

  /**
   * The roles an account holds that the table has no column for. They ride along
   * with every value the shared row produces: that row is columns-only, so any
   * path writing it into an account wholesale revokes these on the next save,
   * with no control on the page that could put them back.
   */
  const unlistedFor = useCallback(
    (accountId) =>
      (baseline.byAccount[accountId]?.roleIds || []).filter(
        (roleId) => !known.includes(roleId),
      ),
    [baseline.byAccount, known],
  );

  /** What each visible account would be saved as right now. */
  const effective = useMemo(() => {
    if (effectiveMode === ALL_MODE) {
      // The shared row supplies the columns; each account keeps whatever it
      // holds that has none. Replacing the entry wholesale here is what used to
      // revoke account-custom roles on the first Apply-to-All save.
      return visibleAccounts.reduce(
        (state, account) => ({
          ...state,
          [account.accountId]: {
            ...shared,
            roleIds: [...unlistedFor(account.accountId), ...shared.roleIds],
          },
        }),
        {},
      );
    }

    return visibleAccounts.reduce(
      (state, account) => ({
        ...state,
        [account.accountId]: byAccount[account.accountId] || emptyEntry(),
      }),
      {},
    );
  }, [byAccount, effectiveMode, shared, unlistedFor, visibleAccounts]);

  const isDirty = useMemo(
    () =>
      visibleAccounts.some((account) => {
        const confirmed = baseline.byAccount[account.accountId];

        return confirmed && !sameEntry(effective[account.accountId], confirmed);
      }),
    [baseline.byAccount, effective, visibleAccounts],
  );

  const isValid = useMemo(
    () =>
      visibleAccounts.every(
        (account) => effective[account.accountId].roleIds.length > 0,
      ),
    [effective, visibleAccounts],
  );

  // ── mutations ─────────────────────────────────────────────────────────────
  //
  // This is where "additive, not replace" lives. Toggling a role adds or removes
  // that one id and moves nothing else, so ticking Fleet Administrator on an
  // account that already holds Report User yields both. The single exception is
  // the tier, whose choices are mutually exclusive by definition.

  const writeEntry = useCallback(
    (accountId, next) => {
      if (effectiveMode === ALL_MODE) {
        setShared(next);
        setSharedTouched(true);

        return;
      }

      setByAccount((current) => ({ ...current, [accountId]: next }));
    },
    [effectiveMode],
  );

  const setCell = useCallback(
    (accountId, cellKey, value) => {
      // In "Apply to All" the table shows one row whose id is 'all', while
      // `effective` is keyed by real account ids — the shared draft is the entry
      // being edited, not any one account's.
      const entry = effectiveMode === ALL_MODE ? shared : effective[accountId];
      if (!entry) {
        return;
      }

      const next = applyCell({ entry, cellKey, value, permissionOptions });
      writeEntry(accountId, next);

      // A row stops complaining as soon as it is touched. In ALL mode the id is
      // the literal 'all' while errors are keyed by account, so the one row
      // clears every account it stands for.
      const clearing =
        effectiveMode === ALL_MODE
          ? visibleAccounts.map((account) => String(account.accountId))
          : [String(accountId)];

      setErrors((current) => {
        const stale = clearing.filter((id) => current.byAccount[id]);

        if (stale.length === 0) {
          return current;
        }

        return {
          ...current,
          byAccount: stale.reduce(
            (cleared, id) => ({ ...cleared, [id]: undefined }),
            current.byAccount,
          ),
        };
      });
    },
    [
      effective,
      effectiveMode,
      permissionOptions,
      shared,
      visibleAccounts,
      writeEntry,
    ],
  );

  /**
   * Custom → All keeps an agreed value, and otherwise opens blank and says so:
   * an intersection downgrades, a union grants, the first account's set is
   * arbitrary, and all three would be written everywhere by a SAVE the admin
   * never composed. Blank fails `isValid`, so SAVE stays disabled by itself.
   *
   * All → Custom seeds the rows only if the shared row was actually edited, so
   * clicking "Apply to All" by accident and back does not blank every row.
   */
  const changeMode = useCallback(
    (nextMode) => {
      if (nextMode === mode) {
        return;
      }

      if (nextMode === ALL_MODE) {
        const entries = visibleAccounts.map(
          (account) => effective[account.accountId],
        );
        const agreed = agreementOf(entries, known);

        setShared(agreed || emptyEntry());
        setSharedTouched(false);
        setNotice(agreed ? null : NOTICES.overwrite);
      } else {
        if (sharedTouched) {
          setByAccount((current) =>
            visibleAccounts.reduce(
              (state, account) => ({
                ...state,
                [account.accountId]: {
                  ...shared,
                  // Same merge `effective` does in All mode: this writes into
                  // `byAccount`, which Custom mode saves verbatim, so `shared`
                  // alone would revoke every column-less role on switching back.
                  roleIds: [
                    ...unlistedFor(account.accountId),
                    ...shared.roleIds,
                  ],
                },
              }),
              current,
            ),
          );
        }

        setNotice(null);
      }

      setMode(nextMode);
    },
    [
      effective,
      known,
      mode,
      shared,
      sharedTouched,
      unlistedFor,
      visibleAccounts,
    ],
  );

  // ── guards ────────────────────────────────────────────────────────────────

  const lockFor = useCallback(
    (accountId, columnType) => {
      if (!canEdit) {
        // No tooltip: the whole page is read-only, and a reason on every control
        // is noise. Matches UserDetailsForm's bare `disabled={!canEdit}`.
        return { disabled: true, reason: null };
      }

      const isRoleColumn = columnType !== 'discoverability';

      // Only roles. An admin editing their own discoverability is not escalating
      // anything, and the rule only ever spoke about roles.
      if (isSelf && isRoleColumn) {
        return { disabled: true, reason: LOCKS.selfModify };
      }

      return null;
    },
    [canEdit, isSelf],
  );

  const groups = useMemo(() => {
    const toRow = (account) => ({
      id: String(account.accountId),
      name: account.name,
      invalid: effective[account.accountId].roleIds.length === 0,
      cells: buildCells({
        columns,
        entry: effective[account.accountId],
        lockFor: (type) => lockFor(account.accountId, type),
        permissionOptions,
        rowName: account.name,
      }),
    });

    if (effectiveMode === ALL_MODE) {
      return [
        {
          id: 'all',
          // The server's own name, which is translated where the constant is an
          // untranslated literal — otherwise this header switches language with
          // the mode. The constant is only the fallback for having no team.
          name: baseline.teams[0]?.name || ALL_TEAMS_ROW,
          rows: [
            {
              id: 'all',
              // The server keys its refusals by real account id, so without
              // these a 422 has no row to render against and the admin gets a
              // bare "not updated" toast with no reason on it.
              accountIds: visibleAccounts.map((account) =>
                String(account.accountId),
              ),
              name: ALL_ACCOUNTS_ROW,
              // What the accounts would be left holding, not what the shared row
              // shows: an account whose only role has no column is not roleless,
              // and flagging it red would contradict isValid and the server.
              invalid: visibleAccounts.some(
                (account) => effective[account.accountId].roleIds.length === 0,
              ),
              cells: buildCells({
                columns,
                entry: shared,
                lockFor: (type) => lockFor('all', type),
                permissionOptions,
                rowName: ALL_ACCOUNTS_ROW,
              }),
            },
          ],
        },
      ];
    }

    return baseline.teams
      .map((team) => ({
        id: team.id,
        name: team.name,
        rows: team.accounts.filter((account) => !account.revoked).map(toRow),
      }))
      .filter((team) => team.rows.length > 0);
  }, [
    baseline.teams,
    columns,
    effective,
    effectiveMode,
    lockFor,
    permissionOptions,
    shared,
    visibleAccounts,
  ]);

  /**
   * Names for the roles with no column, which the server sends for exactly this.
   * `permissionOptions` is the only source of labels the table has, so without
   * them a held role outside it is missing from the header as well as the
   * matrix, and the page claims the user holds less than they do.
   */
  const unlistedNames = useMemo(
    () =>
      baseline.accounts.reduce(
        (names, account) => ({
          ...names,
          [account.accountId]: account.unlistedRoleNames || {},
        }),
        {},
      ),
    [baseline.accounts],
  );

  const rolesFor = useCallback(
    (accountId) => {
      const entry = effective[accountId] || baseline.byAccount[accountId];
      if (!entry) {
        return [];
      }

      const named = unlistedNames[accountId] || {};

      return [
        ...roleLabels(entry.roleIds, permissionOptions),
        ...entry.roleIds
          .filter((roleId) => named[roleId])
          .map((roleId) => named[roleId]),
      ];
    },
    [baseline.byAccount, effective, permissionOptions, unlistedNames],
  );

  // Drives the header tags off the draft, so ticking a role shows its badge
  // before anything is saved. `accountId` rides along because account names are
  // not unique, and grouping on the name alone would fold two accounts that
  // share one into a tag claiming roles neither of them holds together.
  const permissionAssignments = useMemo(
    () =>
      visibleAccounts.flatMap((account) =>
        rolesFor(account.accountId).map((permission) => ({
          accountId: String(account.accountId),
          account: account.name,
          permission,
        })),
      ),
    [rolesFor, visibleAccounts],
  );

  // ── save ──────────────────────────────────────────────────────────────────

  const { dismissAll, notify } = useNotifications();

  // Duplicated from useEditUserForm rather than lifted out of it, since this
  // change adds a feature and does not rewrite PD-4068's hook. dismissAll() is
  // load-bearing: the provider has no dismiss-by-id, so `notify` always prepends
  // a card and reusing an id would put it on two elements.
  const announce = useCallback(
    (intent, title) => {
      dismissAll();
      notify({
        intent,
        title,
        duration: NOTIFICATION_DURATION,
        onClose: () => {},
      });
    },
    [dismissAll, notify],
  );

  const save = useCallback(async () => {
    setIsSaving(true);
    const submitted = effective;
    // The raw draft at submit time; see the merge below for what it answers.
    const sentByAccount = byAccount;

    try {
      const result = await saveAccessPermissions(
        user.id,
        toAccountsPayload(submitted, visibleAccounts),
      );

      if (result.errors) {
        const mapped = toAccessErrors(result.errors, result.errorCodes);
        setErrors(mapped);
        announce('danger', [MESSAGES.failed, ...mapped.base].join(' '));

        return;
      }

      const confirmed = toDraft(result.access);
      setBaseline(confirmed);
      // The controls and the scope radios stay live during a save, so anything
      // touched since submit is kept. `sentByAccount` asks "was this row written
      // since the request went out", the one form of the question that holds in
      // both modes: in All mode an untouched `byAccount` means the server's
      // answer is taken wholesale, and a mid-flight switch to Custom seeds it,
      // so edits after the switch survive.
      //
      // By identity, not by value: an admin can edit a cell and put it back, or
      // switch scope onto the value a row already had, and a value comparison
      // reads both as untouched. Every writer here spreads into a fresh object,
      // so a changed reference means written and nothing else produces one.
      setByAccount((current) =>
        Object.keys(confirmed.byAccount).reduce(
          (next, accountId) =>
            current[accountId] &&
            current[accountId] !== sentByAccount[accountId]
              ? { ...next, [accountId]: current[accountId] }
              : next,
          confirmed.byAccount,
        ),
      );
      setSharedTouched(false);
      setErrors({ base: [], byAccount: {} });
      setNotice(null);
      announce('success', MESSAGES.saved);
    } catch (error) {
      announce('danger', MESSAGES.failed);
    } finally {
      setIsSaving(false);
    }
  }, [announce, byAccount, effective, user, visibleAccounts]);

  return {
    canEdit,
    canSave: isDirty && isValid && !isSaving,
    columns,
    errors,
    groups,
    // No payload, or no role in any segment account: either way there is no
    // table to draw, so the tab stays disabled rather than opening empty.
    // Accounts rather than teams, so an empty group counts as no group.
    isAvailable: Boolean(access) && baseline.accounts.length > 0,
    isSaving,
    isSelf,
    isValid,
    lockFor,
    mode: effectiveMode,
    notice,
    permissionAssignments,
    rolesFor,
    save,
    setCell,
    setMode: changeMode,
    showScopeChoice,
    teams: baseline.teams,
  };
}

// ── pure helpers ────────────────────────────────────────────────────────────

const groupChoiceIds = (option) =>
  (option.choices || []).map((choice) => choice.roleId);

/** Every role id the table can draw, so unknown ones can be left alone. */
const knownRoleIds = (permissionOptions) =>
  permissionOptions.flatMap((option) =>
    option.choices ? groupChoiceIds(option) : [option.roleId],
  );

const roleLabels = (roleIds, permissionOptions) =>
  permissionOptions.flatMap((option) => {
    if (!option.choices) {
      return roleIds.includes(option.roleId) ? [option.label] : [];
    }

    const chosen = option.choices.find((choice) =>
      roleIds.includes(choice.roleId),
    );

    return chosen ? [chosen.label] : [];
  });

const withRole = (roleIds, roleId) =>
  roleIds.includes(roleId) ? roleIds : [...roleIds, roleId];

const withoutRoles = (roleIds, removed) =>
  roleIds.filter((roleId) => !removed.includes(roleId));

/**
 * One cell edit against one entry. Ticking a group grants its first tier and
 * unticking clears every tier, keeping ASSET MGMT. and MGMT. ROLE consistent
 * both ways. Choosing a tier drops the group's other choices — the only replace
 * in the system, and scoped to the group.
 */
function applyCell({ entry, cellKey, value, permissionOptions }) {
  if (DISCOVERABILITY_KEYS.includes(cellKey)) {
    return { ...entry, [cellKey]: value };
  }

  if (cellKey.endsWith(TIER_SUFFIX)) {
    const option = permissionOptions.find(
      (candidate) => `${candidate.id}${TIER_SUFFIX}` === cellKey,
    );

    return {
      ...entry,
      roleIds: withRole(
        withoutRoles(entry.roleIds, groupChoiceIds(option)),
        value,
      ),
    };
  }

  const option = permissionOptions.find(
    (candidate) => candidate.id === cellKey,
  );

  if (option?.choices) {
    const choices = groupChoiceIds(option);

    return {
      ...entry,
      roleIds: value
        ? withRole(entry.roleIds, choices[0])
        : withoutRoles(entry.roleIds, choices),
    };
  }

  return {
    ...entry,
    roleIds: value
      ? withRole(entry.roleIds, option.roleId)
      : withoutRoles(entry.roleIds, [option.roleId]),
  };
}

/**
 * Whether this column holds the account's only role. Counts every role the entry
 * carries, ones with no column included, because that is what the server's 002
 * counts — an account whose only role is account-custom is not one uncheck from
 * empty and must not be reported as such.
 */
const holdsLastRole = (entry, column) => {
  if (entry.roleIds.length !== 1) {
    return false;
  }

  const [only] = entry.roleIds;

  return column.type === 'group'
    ? groupChoiceIds(column.option).includes(only)
    : column.option?.roleId === only;
};

function buildCells({ columns, entry, lockFor, permissionOptions, rowName }) {
  return columns
    .filter((column) => column.key !== ACCOUNT_COLUMN)
    .map((column) => {
      const lock = lockFor(column.type);
      // The accessible name, and what makes the tests readable:
      // getByRole('checkbox', { name: 'Fleet Admin for Amazon Logistics' }).
      const label = `${column.title} for ${rowName}`;

      if (column.type === 'discoverability') {
        return {
          key: column.key,
          type: column.type,
          label,
          checked: Boolean(entry[column.key]),
          disabled: Boolean(lock?.disabled),
          lockReason: lock?.reason || null,
        };
      }

      if (column.type === 'tier') {
        const chosen = (column.option.choices || []).find((choice) =>
          entry.roleIds.includes(choice.roleId),
        );

        return {
          key: column.key,
          type: column.type,
          label,
          value: chosen ? { roleId: chosen.roleId, label: chosen.label } : null,
          options: column.option.choices || [],
          disabled: Boolean(lock?.disabled),
          lockReason: lock?.reason || null,
        };
      }

      const checked =
        column.type === 'group'
          ? groupChoiceIds(column.option).some((roleId) =>
              entry.roleIds.includes(roleId),
            )
          : entry.roleIds.includes(column.option.roleId);

      // Removing the last role is PD-4069's flow. Refusing here rather than
      // waiting for the server's 422 shows the dead end before it is reached.
      const lastRole = checked && holdsLastRole(entry, column);

      return {
        key: column.key,
        type: column.type,
        label,
        checked,
        disabled: Boolean(lock?.disabled) || lastRole,
        lockReason: lock?.reason || (lastRole ? LOCKS.lastRole : null),
      };
    });
}

const toAccountsPayload = (effective, visibleAccounts) =>
  visibleAccounts.map((account) => {
    const entry = effective[account.accountId];

    return {
      account_id: account.accountId,
      role_ids: entry.roleIds,
      discoverability: {
        visible_as_external_recipient: entry.visibleAsExternalRecipient,
        visible_as_internal_recipient: entry.visibleAsInternalRecipient,
      },
    };
  });

export { knownRoleIds, toAccountsPayload };

export default useAccessPermissionsForm;
